import { graphql } from 'gatsby';
import React from 'react';
import { BackgroundColorTheme } from '../templates/Page';
import AccordionModule, { AccordionModuleProps } from './ui/modules/AccordionModule';
import CtaModule, { CtaModuleProps } from './ui/modules/CtaModule';
import ExperienceCardModule, { ExperienceCardModuleProps } from './ui/modules/ExperienceCardModule';
import FeaturedInModule, { FeaturedInModuleProps } from './ui/modules/FeaturedInModule';
import LogosModule, { LogosModuleProps } from './ui/modules/LogosModule';
import PhotoGalleryModule, { PhotoGalleryModuleProps } from './ui/modules/PhotoGalleryModule';
import RichTextModule, { RichTextModuleProps } from './ui/modules/RichTextModule';
import TextAndCarouselModule, {
  TextAndCarouselModuleProps,
} from './ui/modules/TextAndCarouselModule';
import TextAndImagesModule, { TextAndImagesModuleProps } from './ui/modules/TextAndImagesModule';
import ThemeModule, { ThemeModuleProps } from './ui/modules/ThemeModule';

export const ModulesFragment = graphql`
  fragment Modules on SanityAccordionModuleOrCtaModuleOrExperienceCardModuleOrFeaturedInModuleOrLogosModuleOrPhotoGalleryModuleOrRichTextModuleOrTextAndCarouselModuleOrTextAndImagesModuleOrThemeModule {
    __typename
    ...AccordionModule
    ... on SanityAccordionModule {
      moduleId
      disabled
    }
    ...CtaModule
    ... on SanityCtaModule {
      moduleId
      disabled
    }
    ...ExperienceCardModule
    ... on SanityExperienceCardModule {
      moduleId
      disabled
    }
    ...FeaturedInModule
    ... on SanityFeaturedInModule {
      moduleId
      disabled
    }
    ...LogosModule
    ... on SanityLogosModule {
      moduleId
      disabled
    }
    ...PhotoGalleryModule
    ... on SanityPhotoGalleryModule {
      moduleId
      disabled
    }
    ...RichTextModule
    ... on SanityRichTextModule {
      moduleId
      disabled
    }
    ...TextAndCarouselModule
    ... on SanityTextAndCarouselModule {
      moduleId
      disabled
    }
    ...TextAndImagesModule
    ... on SanityTextAndImagesModule {
      moduleId
      disabled
    }
    ...ThemeModule
    ... on SanityThemeModule {
      moduleId
      disabled
    }
  }
`;

export interface ModulesContentProps {
  pageTheme: BackgroundColorTheme;
  modules: Array<Module>;
}

export interface CommonModuleProps {
  moduleId?: string;
  disabled?: boolean | null;
  theme: BackgroundColorTheme;
  className?: string;
}

export type Module =
  | (AccordionModuleProps & CommonModuleProps & { __typename: 'SanityAccordionModule' })
  | (CtaModuleProps & CommonModuleProps & { __typename: 'SanityCtaModule' })
  | (ExperienceCardModuleProps & CommonModuleProps & { __typename: 'SanityExperienceCardModule' })
  | (FeaturedInModuleProps & CommonModuleProps & { __typename: 'SanityFeaturedInModule' })
  | (LogosModuleProps & CommonModuleProps & { __typename: 'SanityLogosModule' })
  | (PhotoGalleryModuleProps & CommonModuleProps & { __typename: 'SanityPhotoGalleryModule' })
  | (RichTextModuleProps & CommonModuleProps & { __typename: 'SanityRichTextModule' })
  | (TextAndCarouselModuleProps & CommonModuleProps & { __typename: 'SanityTextAndCarouselModule' })
  | (TextAndImagesModuleProps & CommonModuleProps & { __typename: 'SanityTextAndImagesModule' })
  | (ThemeModuleProps & CommonModuleProps & { __typename: 'SanityThemeModule' });

function ModulesContent({ modules, pageTheme }: ModulesContentProps): React.ReactElement {
  const enabledModules = modules.filter(module => !module.disabled);

  return (
    <>
      {enabledModules.map((module, moduleIndex) => {
        const moduleKey = 'module-' + moduleIndex;

        const commonModuleProps: CommonModuleProps = {
          theme:
            enabledModules
              .slice(0, moduleIndex)
              .reverse()
              .find(module => module.__typename === 'SanityThemeModule')?.theme || pageTheme,
        };

        if (module.__typename === 'SanityAccordionModule') {
          return (
            <AccordionModule key={moduleKey} {...commonModuleProps} {...module}></AccordionModule>
          );
        }
        if (module.__typename === 'SanityCtaModule') {
          return <CtaModule key={moduleKey} {...commonModuleProps} {...module}></CtaModule>;
        }
        if (module.__typename === 'SanityExperienceCardModule') {
          return (
            <ExperienceCardModule
              key={moduleKey}
              {...commonModuleProps}
              {...module}
            ></ExperienceCardModule>
          );
        }
        if (module.__typename === 'SanityFeaturedInModule') {
          return (
            <FeaturedInModule key={moduleKey} {...commonModuleProps} {...module}></FeaturedInModule>
          );
        }
        if (module.__typename === 'SanityLogosModule') {
          return <LogosModule key={moduleKey} {...commonModuleProps} {...module}></LogosModule>;
        }
        if (module.__typename === 'SanityPhotoGalleryModule') {
          return (
            <PhotoGalleryModule
              key={moduleKey}
              {...commonModuleProps}
              {...module}
            ></PhotoGalleryModule>
          );
        }
        if (module.__typename === 'SanityRichTextModule') {
          return (
            <RichTextModule key={moduleKey} {...commonModuleProps} {...module}></RichTextModule>
          );
        }
        if (module.__typename === 'SanityTextAndCarouselModule') {
          return (
            <TextAndCarouselModule
              key={moduleKey}
              {...commonModuleProps}
              {...module}
            ></TextAndCarouselModule>
          );
        }
        if (module.__typename === 'SanityTextAndImagesModule') {
          return (
            <TextAndImagesModule
              key={moduleKey}
              {...commonModuleProps}
              {...module}
            ></TextAndImagesModule>
          );
        }
        if (module.__typename === 'SanityThemeModule') {
          return <ThemeModule key={moduleKey} {...commonModuleProps} {...module}></ThemeModule>;
        }

        // @ts-expect-error
        throw new Error('Got unexpected typename: ' + module.__typename);
      })}
    </>
  );
}

export default ModulesContent;
